import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShoppingBag, Palette } from "lucide-react";
import { LOGO_URL, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from;
  const [role, setRole] = useState("customer");
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({
    name: "", email: "", password: "", phone: "",
    address_line: "", city: "", postal_code: "", country: "France", building: "", building_code: "",
    bio: "", iban: "", bic: "", bank_holder: "", photo: "", style: "", personality: "",
  });
  const up = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email || !f.password) {
      toast.error("Nom, email et mot de passe sont obligatoires");
      return;
    }
    if (role === "artist" && !f.iban) {
      toast.error("L'IBAN est requis pour recevoir vos paiements");
      return;
    }
    setLoading(true);
    const payload = { ...f, role, photos: f.photo ? [f.photo] : [] };
    try {
      const user = await register(payload);
      toast.success("Compte créé avec succès");
      if (from) navigate(from);
      else navigate(user.role === "artist" ? "/compte" : "/compte");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 pt-28 pb-16" data-testid="register-page">
      <div className="w-full max-w-lg">
        <div className="flex flex-col items-center mb-8">
          <img src={LOGO_URL} alt="Logo" className="h-14 w-14 object-contain mb-4 rounded-full" />
          <h1 className="font-display text-4xl text-white">Créer un compte</h1>
        </div>

        {/* Role toggle */}
        <div className="grid grid-cols-2 gap-3 mb-8">
          <button data-testid="role-customer" onClick={() => setRole("customer")}
            className={`flex items-center gap-3 p-4 border transition-all ${role === "customer" ? "border-[#2563EB] bg-[#2563EB]/10 text-white" : "border-white/15 text-zinc-400"}`}>
            <ShoppingBag className="h-5 w-5" /> Je suis acheteur
          </button>
          <button data-testid="role-artist" onClick={() => setRole("artist")}
            className={`flex items-center gap-3 p-4 border transition-all ${role === "artist" ? "border-[#2563EB] bg-[#2563EB]/10 text-white" : "border-white/15 text-zinc-400"}`}>
            <Palette className="h-5 w-5" /> Je suis artiste
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <Input testid="reg-name" value={f.name} onChange={up("name")} placeholder={role === "artist" ? "Nom d'artiste *" : "Nom complet *"} />
          <Input testid="reg-email" type="email" value={f.email} onChange={up("email")} placeholder="Email *" />
          <Input testid="reg-password" type="password" value={f.password} onChange={up("password")} placeholder="Mot de passe *" />
          <Input testid="reg-phone" value={f.phone} onChange={up("phone")} placeholder="Téléphone" />

          {role === "customer" && (
            <>
              <Input testid="reg-photo-customer" value={f.photo} onChange={up("photo")} placeholder="Photo de profil (URL, optionnel)" />
              <Input testid="reg-address" value={f.address_line} onChange={up("address_line")} placeholder="Adresse de livraison" />
              <div className="grid grid-cols-2 gap-4">
                <Input testid="reg-postal" value={f.postal_code} onChange={up("postal_code")} placeholder="Code postal" />
                <Input testid="reg-city" value={f.city} onChange={up("city")} placeholder="Ville" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input testid="reg-building" value={f.building} onChange={up("building")} placeholder="Bâtiment / Étage" />
                <Input testid="reg-building-code" value={f.building_code} onChange={up("building_code")} placeholder="Code d'entrée / interphone" />
              </div>
              <Input testid="reg-country" value={f.country} onChange={up("country")} placeholder="Pays" />
            </>
          )}

          {role === "artist" && (
            <>
              <Input testid="reg-style" value={f.style} onChange={up("style")} placeholder="Votre style artistique (ex. Expressionnisme, couleurs vives)" />
              <textarea data-testid="reg-bio" value={f.bio} onChange={up("bio")} placeholder="Présentation de l'artiste (votre parcours, votre handicap, votre démarche…)" rows={3}
                className="w-full bg-[#0d0d0d] border border-white/15 px-4 py-3 text-white focus:border-[#2563EB] outline-none resize-none" />
              <textarea data-testid="reg-personality" value={f.personality} onChange={up("personality")} placeholder="Décrivez votre personnalité (en une phrase inspirante)" rows={2}
                className="w-full bg-[#0d0d0d] border border-white/15 px-4 py-3 text-white focus:border-[#2563EB] outline-none resize-none" />
              <Input testid="reg-photo" value={f.photo} onChange={up("photo")} placeholder="URL d'une photo de vous (optionnel)" />
              <div className="border border-white/10 p-4 space-y-4">
                <p className="text-xs uppercase tracking-widest text-[#60a5fa]">Coordonnées bancaires (pour encaisser vos ventes)</p>
                <Input testid="reg-holder" value={f.bank_holder} onChange={up("bank_holder")} placeholder="Titulaire du compte" />
                <Input testid="reg-iban" value={f.iban} onChange={up("iban")} placeholder="IBAN *" />
                <Input testid="reg-bic" value={f.bic} onChange={up("bic")} placeholder="BIC" />
              </div>
            </>
          )}

          <button data-testid="register-submit" type="submit" disabled={loading}
            className="w-full inline-flex items-center justify-center gap-3 bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-60 text-white px-10 py-3 text-sm tracking-wide transition-colors">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} Créer mon compte
          </button>
        </form>
        <p className="text-zinc-500 text-sm text-center mt-8">
          Déjà inscrit ?{" "}
          <Link to="/connexion" state={{ from }} className="text-[#60a5fa] hover:underline" data-testid="go-login">Se connecter</Link>
        </p>
      </div>
    </div>
  );
}

const Input = ({ testid, type = "text", value, onChange, placeholder }) => (
  <input data-testid={testid} type={type} value={value} onChange={onChange} placeholder={placeholder}
    className="w-full bg-[#0d0d0d] border border-white/15 px-4 py-3 text-white focus:border-[#2563EB] outline-none" />
);
